import { FC, useEffect, useMemo, useState } from 'react';
import { SingleValue } from 'react-select';
import { useRouter } from 'next/router';
import { useField, useFormikContext } from 'formik';
import { DataEntity, generateOptionValueFromEntity } from 'components/PlatformResourceSelector/utils';
import { usePlatformResourceSelector, OptionValue } from 'components/PlatformResourceSelector/usePlatformResourceSelector';
import { useDataCollection } from 'modules/AnalyticsCollection/AnalyticsCollectionProvider';
import { PlatformResourceSelector } from 'components/PlatformResourceSelector';
import { PlaygroundConfig } from 'modules/Playground/types/playgroundConfig';
import { useFeaturedModels } from 'modules/Playground/api/hooks/useFeaturedModels';
import { ApiModel } from 'types/swagger/clarifai-api';
import { splitByLastDoubleUnderscore } from 'modules/WorkflowV2/utils/splitByLastDoubleUnderscore';
import { useIsLoggedIn } from 'modules/Auth/hooks';
import { usePlaygroundModelSelectorChange } from 'modules/Playground/hooks/utils/usePlaygroundModelSelectorChange';
import { useDataForPlayground } from './useDataForPlayground';

export const PlaygroundModelSelector: FC = () => {
  const router = useRouter();
  const { track } = useDataCollection();
  const [{ value: selectedModel }, , { setValue: setSelectedModel }] = useField<PlaygroundConfig['model']>('model');
  const [, , { setValue: setSelectedModelVersion }] = useField<PlaygroundConfig['modelVersion']>('modelVersion');
  const { setFieldValue } = useFormikContext<PlaygroundConfig>();
  const isLoggedIn = useIsLoggedIn();
  const { handleModelChange } = usePlaygroundModelSelectorChange();

  const modelIdVersionQueryParam = router.query.model as string | undefined;
  const featuredModels = useFeaturedModels(); // useQuery cache ⚡️

  // NOTE: Make an additional API call if the requested model is not featured (i.e. fetch it)
  const [modelIdFromQueryParam] = splitByLastDoubleUnderscore(modelIdVersionQueryParam);
  const modelQueryIsFeaturedModel = featuredModels?.some(({ id }) => id === modelIdFromQueryParam);
  const defaultSearchValue = !modelQueryIsFeaturedModel ? modelIdFromQueryParam : '';

  const [searchValue, setSearchValue] = useState(defaultSearchValue);

  const fromCommunity = !!searchValue || !isLoggedIn;
  const dataOptions = useDataForPlayground({ fromCommunity, searchParam: searchValue });
  const modelIdentifierKey = generateOptionValueFromEntity(selectedModel as unknown as CF.API.Models.Model);
  const prsData = usePlatformResourceSelector(dataOptions, modelIdentifierKey);

  // PRS Search Input Entered
  const onSearchInputChanged = (newSearchValue: string) => {
    setSearchValue(newSearchValue);
  };

  // PRS Selection –> Playground Handler
  const onSelectedModelChanged = (selectedOption: SingleValue<OptionValue>) => {
    handlePlaygroundModelChange(selectedOption?.value as string);
  };

  const handlePlaygroundModelChange = (selectedId: string, isDefault = false) => {
    const currentSelectedId = generateOptionValueFromEntity(selectedModel as unknown as CF.API.Models.Model);
    if (selectedId !== currentSelectedId) {
      const model =
        prsData?.data?.indexed?.[selectedId] ||
        featuredModels?.find((modelItem) => generateOptionValueFromEntity(modelItem as unknown as DataEntity) === selectedId);

      if (!model) return; // 🛡️ APIs likely aren't loaded yet...

      // Set the model query to the selected model value, without reloading the page:
      const currentModel: ApiModel = model as unknown as ApiModel;

      // Do not track default model selection
      if (!isDefault) {
        track('Playground -> Model Selected', { modelId: model.id });
      }

      // Update query params:
      handleModelChange(currentModel as unknown as CF.API.Models.Model);

      // set form model values
      setSelectedModel(currentModel);
      setSelectedModelVersion(currentModel.model_version);
      setFieldValue('deploymentId', undefined);
    }
  };

  const selectedModelValue = useMemo(() => {
    if (selectedModel) return generateOptionValueFromEntity(selectedModel as unknown as CF.API.Models.Model);
    return undefined;
  }, [selectedModel]);

  // Set initial model when playground is loaded
  useEffect(() => {
    if (!featuredModels?.length || prsData.isLoading) return;

    if (!modelIdVersionQueryParam) {
      const defaultModel = featuredModels?.[0];
      const defaultQueryValue = generateOptionValueFromEntity(defaultModel as unknown as DataEntity);
      handlePlaygroundModelChange(defaultQueryValue, true);
      return;
    }

    if (modelIdVersionQueryParam && !selectedModel && !selectedModelValue) {
      handlePlaygroundModelChange(modelIdVersionQueryParam);
    }
  }, [modelIdVersionQueryParam, featuredModels, prsData.isLoading]);

  // Set default model when no model is selected and no query param is present
  useEffect(() => {
    if (!selectedModel && !prsData.isLoading && !modelIdVersionQueryParam) {
      const defaultModel = featuredModels?.[0];
      const defaultQueryValue = generateOptionValueFromEntity(defaultModel as unknown as DataEntity);
      handlePlaygroundModelChange(defaultQueryValue, true);
    }
  }, [selectedModel, prsData.isLoading, modelIdVersionQueryParam]);

  return <PlatformResourceSelector {...prsData} onChange={onSelectedModelChanged} onInputChange={onSearchInputChanged} />;
};
